/**
 * Client analytics batcher.
 *
 * - Reads `REACT_APP_ANALYTICS` from env; when `off`, all calls are no-ops
 *   (no network requests).
 * - Batches events (max 20 per POST, 5s flush interval).
 * - Attaches `session_id` from `authStore` on every event and via the
 *   `X-Session-Id` header (handled in `api.js`).
 * - Never throws — errors are swallowed.
 *
 * Only UI/session/algorithm-internal telemetry belongs here. Do NOT re-emit
 * events the server already writes into the ledger (mission.*, approval.*,
 * focus.item.*, cost.*, etc.). Use `ui.<verb>.clicked` for pre-server clicks.
 */
import api from "./api";

const ANALYTICS_ON = (process.env.REACT_APP_ANALYTICS || "on").toLowerCase() !== "off";
const MAX_BATCH = 20;
const FLUSH_MS = 5000;

let queue = [];
let flushTimer = null;

function getSessionId() {
  try {
    return sessionStorage.getItem("cl.session_id");
  } catch {
    return null;
  }
}

function schedule() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_MS);
}

async function flush() {
  flushTimer = null;
  if (!ANALYTICS_ON) {
    queue = [];
    return;
  }
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH);
  try {
    await api.post("/analytics/ingest", { events: batch });
  } catch {
    /* drop silently — analytics is best-effort */
  }
  if (queue.length > 0) schedule();
}

/**
 * Fire-and-forget event.
 * `event` is dot-namespaced (e.g. "ui.tile.clicked").
 * `properties` is a freeform dict; the server strips PII-ish keys.
 */
export function track(event, properties = {}) {
  if (!ANALYTICS_ON) return;
  const session_id = getSessionId();
  // Category is inferred from event namespace.
  let category = "ui";
  if (event.startsWith("ui.")) category = "ui";
  else if (event.startsWith("session.")) category = "session";
  else if (event.startsWith("attention.")) category = "attention";
  else if (event.startsWith("system.")) category = "system";
  queue.push({
    event,
    category,
    properties,
    session_id,
    ts: new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) {
    flush();
  } else {
    schedule();
  }
}

/**
 * Convenience: page view. Called once per route mount.
 */
export function trackPageView(route) {
  track("ui.page_view", {
    route,
    viewport_kind: window.innerWidth < 768 ? "mobile" : "desktop",
  });
}

// Best-effort flush before the tab unloads.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (queue.length && ANALYTICS_ON) {
      try {
        const blob = new Blob(
          [JSON.stringify({ events: queue.splice(0, MAX_BATCH) })],
          { type: "application/json" },
        );
        navigator.sendBeacon?.(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/ingest`, blob);
      } catch { /* ignore */ }
    }
  });
}
